package org.example.project.screens_shared

import org.example.project.manager.NodeScope

import androidx.compose.runtime.*
import org.example.project.dashboard.DashboardNode
import org.example.project.dashboard.ScreenManager
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import org.example.project.cleaning.SharedWorkspaceStyle
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.service.LoginService
import org.example.project.service.LizenzService
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.service.RollenService
import org.example.project.settings.*
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import kotlin.math.ceil

private const val KSV_MIN_COL_W = 480
private const val KSV_BLOCK_GAP = 32

private val karteiRollenJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun karteiRollenApiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

private suspend fun loadKarteiRollen(): List<All> {
    val userId = LoginService.getId()
    if (userId.isBlank()) return emptyList()
    val raw = runCatching { LumaticaApiManager.call("karteiseitenverwaltungscreen/user/${karteiRollenApiPart(userId)}") }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { karteiRollenJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private suspend fun saveKarteiRolle(role: All): All? {
    val userId = LoginService.getId()
    if (userId.isBlank() || role.id.isBlank()) return null
    val response = runCatching {
        window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/karteiseitenverwaltungscreen/save-json",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = karteiRollenJson.encodeToString(
                    buildJsonObject {
                        put("userId", userId)
                        put("role", karteiRollenJson.encodeToJsonElement(All.AllNames.serializer(), role))
                    }
                )
            )
        ).await()
    }.getOrNull() ?: return null
    if (!response.ok) return null
    val raw = response.text().await()
    if (raw.isBlank() || raw == "null") return null
    return runCatching { karteiRollenJson.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()
}

private val BERECHTIGUNG_OPTIONS = listOf(
    All(key = RollenService.KEINE,     label = "Deaktiviert"),
    All(key = RollenService.LESEN,     label = "Lesen"),
    All(key = RollenService.SCHREIBEN, label = "Schreiben"),
    All(key = RollenService.ALLES,     label = "Alles"),
)

private fun gridFor(maxPages: Int) =
    "170px 160px ${List(maxPages) { "140px" }.joinToString(" ")}"

@Composable
fun SHAREDKarteiSettings(art: String = org.example.project.service.currentSaveArt()) {
    Style(SharedWorkspaceStyle)
    val scope = rememberCoroutineScope()
    val internalTitleVisible = LocalScreenInternalTitleVisible.current

    var rollen          by remember { mutableStateOf(emptyList<All>()) }
    var selectedRolleId by remember { mutableStateOf("") }
    var draftRolle      by remember { mutableStateOf<All?>(null) }
    var numCols         by remember { mutableStateOf(1) }
    var containerEl     by remember { mutableStateOf<org.w3c.dom.Element?>(null) }

    suspend fun reloadRoles() {
        rollen = loadKarteiRollen()
        selectedRolleId = rollen.firstOrNull()?.id ?: ""
        draftRolle = rollen.firstOrNull()
    }

    suspend fun refreshCurrentRole() {
        RollenService.refreshCurrentUserRole(art)
    }

    LaunchedEffect(art) {
        reloadRoles()
    }

    LaunchedEffect(selectedRolleId) {
        draftRolle = rollen.firstOrNull { it.id == selectedRolleId }
    }

    fun measure() {
        val w = containerEl?.getBoundingClientRect()?.width ?: return
        if (w <= 0) return
        numCols = ((w + KSV_BLOCK_GAP) / (KSV_MIN_COL_W + KSV_BLOCK_GAP)).toInt().coerceIn(1, 4)
    }

    DisposableEffect(Unit) {
        val listener: (org.w3c.dom.events.Event) -> Unit = { measure() }
        window.addEventListener("resize", listener)
        onDispose { window.removeEventListener("resize", listener) }
    }

    val effectiveNode = remember(art) {
        when (art) {
            org.example.project.manager.NodeScope.CUS -> DashboardNode.CUS
            else -> DashboardNode.SYS
        }
    }
    val visibleScreenKeys = remember(effectiveNode) {
        when (effectiveNode) {
            DashboardNode.CUS -> LizenzService.getEffectiveScreens().toSet()
            DashboardNode.SYS -> ScreenManager.allScreenKeys(effectiveNode).toSet()
            else -> emptySet()
        }
    }

    val karteis = visibleScreenKeys
        .map { key -> key to ScreenManager.karteiPagesForKey(key) }
        .filter { (_, pages) -> pages.isNotEmpty() }
        .sortedBy { (key, _) -> key }

    val columns = if (karteis.isEmpty() || numCols == 0) listOf(emptyList())
    else karteis.chunked(ceil(karteis.size / numCols.toDouble()).toInt().coerceAtLeast(1))

    fun setBerechtigung(screenKey: String, value: String) {
        draftRolle = draftRolle?.let { r ->
            r.copy(screenBerechtigungen = r.screenBerechtigungen + (screenKey to value))
        }
    }

    fun setPageVisible(screenKey: String, pageId: String, visible: Boolean) {
        draftRolle = draftRolle?.let { r ->
            r.copy(karteiSeiten = r.karteiSeiten + ("${screenKey}::${pageId}" to visible.toString()))
        }
    }

    Div({ classes(SharedWorkspaceStyle.workspace) }) {
        Div({ classes(SharedWorkspaceStyle.side, SharedWorkspaceStyle.content); style { width(100.percent) } }) {
            Div({
                classes(SharedWorkspaceStyle.contentInner)
                style { maxWidth(100.percent); overflowY("auto"); flex(1); minHeight(0.px) }
            }) {

                if (internalTitleVisible) {
                    Div({ classes(SharedWorkspaceStyle.rightTitle) }) {
                        Span({ classes(SharedWorkspaceStyle.sectionTitle) }) { Text("Kartei Seitenverwaltung") }
                    }
                }

                // ── Aktionsbar ────────────────────────────────────────────
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        alignItems(AlignItems.Center)
                        gap(22.px)
                    }
                }) {
                    AppDropdown(
                        options = rollen.map { All(key = it.id, label = it.name) },
                        selectedValue = selectedRolleId.ifBlank { null },
                        onChange = { selectedRolleId = it },
                        placeholder = "Rolle wählen…",
                        attrs = { style { width(200.px); height(42.px) } }
                    )
                    LumaticaButton(
                        label = "Alle Sichtbar",
                        onClick = {
                            val draft = draftRolle ?: return@LumaticaButton
                            val seiten = draft.karteiSeiten.toMutableMap()
                            karteis.forEach { (sk, pages) ->
                                pages.forEach { p -> seiten["${sk}::${p.id}"] = "true" }
                            }
                            draftRolle = draft.copy(karteiSeiten = seiten)
                        },
                        variant = LumaticaButtonVariant.Standard, heightPx = 42
                    )
                    LumaticaButton(
                        label = "Alle Aus",
                        onClick = {
                            val draft = draftRolle ?: return@LumaticaButton
                            val seiten = draft.karteiSeiten.toMutableMap()
                            karteis.forEach { (sk, pages) ->
                                pages.forEach { p -> seiten["${sk}::${p.id}"] = "false" }
                            }
                            draftRolle = draft.copy(karteiSeiten = seiten)
                        },
                        variant = LumaticaButtonVariant.Standard, heightPx = 42
                    )
                    LumaticaButton(
                        label = "Speichern",
                        onClick = {
                            scope.launch {
                                val r = draftRolle ?: return@launch
                                saveKarteiRolle(r)
                                refreshCurrentRole()
                                reloadRoles()
                                selectedRolleId = r.id
                            }
                        },
                        variant = LumaticaButtonVariant.Action, heightPx = 42
                    )
                }

                // ── Divider ──────────────────────────────────────────────
                Div({
                    style {
                        alignSelf("center")
                        width(600.px); maxWidth(100.percent)
                        height(1.px)
                        backgroundColor(rgba(255, 255, 255, 0.14))
                        marginTop(40.px); marginBottom(40.px)
                    }
                })

                if (karteis.isEmpty()) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.Center)
                            color(Color("#6b7280"))
                            fontSize(14.px)
                            padding(40.px)
                        }
                    }) { Text("Keine Kartei-Screens registriert.") }
                } else {
                    val maxPages = karteis.maxOfOrNull { it.second.size } ?: 1
                    val grid = gridFor(maxPages)

                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            property("width", "fit-content")
                            minWidth(400.px)
                        }
                    }) {
                        // Einmaliger Header
                        Div({
                            classes(SharedWorkspaceStyle.permissionHeader)
                            style { gridTemplateColumns(grid) }
                        }) {
                            Span { Text("Screenname") }
                            Span { Text("Berechtigung") }
                            repeat(maxPages) { i ->
                                Span { Text("Seite ${i + 1}") }
                            }
                        }

                        // Eine Datenzeile pro Kartei-Screen
                        karteis.forEach { (screenKey, pages) ->
                            val berechtigung = draftRolle
                                ?.screenBerechtigungen?.get(screenKey)
                                ?: RollenService.KEINE

                            Div({
                                classes(SharedWorkspaceStyle.permissionRow)
                                style { gridTemplateColumns(grid) }
                            }) {
                                Span({ classes(SharedWorkspaceStyle.permissionName) }) {
                                    Text(ScreenManager.labelForKey(screenKey))
                                }
                                AppDropdown(
                                    options = BERECHTIGUNG_OPTIONS,
                                    selectedValue = berechtigung,
                                    onChange = { setBerechtigung(screenKey, it) },
                                    appearance = AppDropdownAppearance.Underline,
                                    attrs = {
                                        classes(SharedWorkspaceStyle.permissionSelect)
                                        style { width(140.px) }
                                    }
                                )
                                // Seiten: Checkbox + Name inline, leere Zelle wenn keine Seite
                                repeat(maxPages) { i ->
                                    val page = pages.getOrNull(i)
                                    if (page != null) {
                                        val visible = draftRolle
                                            ?.karteiSeiten?.get("${screenKey}::${page.id}") != "false"
                                        val pageLabel = page.bezeichnung.ifBlank { page.id }
                                        Div({
                                            style {
                                                display(DisplayStyle.Flex)
                                                alignItems(AlignItems.Center)
                                                gap(6.px)
                                            }
                                        }) {
                                            LumaticaCheckbox(
                                                checked = visible,
                                                onChange = { setPageVisible(screenKey, page.id, it) }
                                            )
                                            Span({
                                                style {
                                                    fontSize(13.px)
                                                    color(Color.white)
                                                    property("white-space", "nowrap")
                                                }
                                            }) { Text(pageLabel) }
                                        }
                                    } else {
                                        Span {}
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
